import type { SoundConfig, SoundPresetId } from "../../types/timer-settings";
import { SOUND_PRESETS, playSound } from "../../utils/sound-presets";
import { CustomSelect } from "../../../../ui/atoms/CustomSelect";
import { PlayIcon } from "../../../../ui/icons";

interface DurationSliderProps {
  readonly label: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly unit: string;
  readonly onChange: (value: number) => void;
}

export function DurationSlider({ label, value, min, max, unit, onChange }: DurationSliderProps) {
  return (
    <label className="flex items-center gap-3">
      <span className="text-xs text-ink-muted w-28 shrink-0">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 h-1.5 accent-accent cursor-pointer"
      />
      <span className="text-xs font-mono text-ink w-14 text-right tabular-nums">
        {value} {unit}
      </span>
    </label>
  );
}

interface ToggleSwitchProps {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}

export function ToggleSwitch({ label, checked, onChange }: ToggleSwitchProps) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-xs text-ink-muted">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors ${checked ? "bg-accent" : "bg-border"}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`}
        />
      </button>
    </label>
  );
}

interface SoundConfigRowProps {
  readonly label: string;
  readonly config: SoundConfig;
  readonly onChange: (config: SoundConfig) => void;
}

export function SoundConfigRow({ label, config, onChange }: SoundConfigRowProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] text-ink-faint uppercase tracking-wide">{label}</span>
      <div className="flex items-center gap-2">
        <CustomSelect
          value={config.preset}
          onChange={(val) => onChange({ ...config, preset: val as SoundPresetId })}
          options={SOUND_PRESETS.map((p) => ({ value: p.id, label: p.label }))}
          className="w-36"
        />
        <input
          type="range"
          min="0"
          max="100"
          value={config.volume}
          onChange={(e) => onChange({ ...config, volume: Number(e.target.value) })}
          className="flex-1 h-1.5 accent-accent cursor-pointer"
        />
        <span className="text-xs font-mono text-ink-muted w-8 text-right tabular-nums">
          {config.volume}%
        </span>
        <button
          type="button"
          onClick={() => playSound(config)}
          title="Apercu"
          className="w-7 h-7 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-surface-alt rounded-md"
        >
          <PlayIcon className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
